import { ActionId, StepId } from 'config/flow';
import { createFlow } from '@wix/gg-common-lib/orchestrator';
import { StepParams, StepRenderPart } from 'types/steps';
import { SelectStoreTypeStep } from 'steps/select-store-type/SelectStoreTypeStep';
import { SelectSellingTypeStep } from 'steps/select-selling-type/SelectSellingTypeStep';
import { SelectCategoryStep } from 'steps/select-category/SelectCategoryStep';
import { SelectCatalogSizeStep } from 'steps/select-catalog-size/SelectCatalogSizeStep';
import { SelectTemplateStep } from 'steps/select-template/SelectTemplateStep';
import { SelectTemplateStepPreview } from 'steps/select-template/components/SelectTemplateStepPreview';
import { useAppStore } from 'stores/state';
import { CatalogSize, Category, StoreType } from 'config/enums';
import { CurrentPlatformStep } from 'steps/current-platform/CurrentPlatformStep';
import { SelectSiteNameStep } from 'steps/select-site-name/SelectSiteNameStep';
import { SiteSetupStep } from 'steps/site-setup/SiteSetupStep';
import { SiteReadyStep } from 'steps/site-ready/SiteReadyStep';
import { redirects } from 'api/redirects';
import { isValidURL, matchUrlParam, toValidURL } from '@wix/gg-common-lib/utils';
import { useIsMobile } from '@wix/gg-common-lib/hooks';
import { SiteReadyStepMobile } from 'steps/site-ready/components/SiteReadyStepMobile';
import { LandingPageStep } from 'steps/landing-page/LandingPageStep';
import { CreateProductUploadStep } from 'steps/create-product/CreateProductUploadStep';
import { useFeatureToggle } from './useFeatureToggle';
import { FeatureToggle } from 'config/experiments';
import { useProductStore } from 'stores/product';
import { templatesByCategory } from 'config/templates';
import { CreateProductFormStep } from 'steps/create-product/CreateProductFormStep';

export const useFlowBuilder = () => {
  const isMobile = useIsMobile();
  const storeType = useAppStore((state) => state.storeType);
  const catalogSize = useAppStore((state) => state.catalogSize);
  const isCreateSingleProductEnabled = useFeatureToggle(FeatureToggle.CreateSingleProduct);
  const shouldAddSingleProductToStore = useProductStore((state) => state.shouldAddSingleProductToStore);

  const flow = createFlow<StepParams, StepRenderPart>();

  if (!matchUrlParam('skipLP', 'true')) {
    flow.step(StepId.LandingPage, LandingPageStep, { hideProgress: true });
  }

  flow.step(StepId.SelectStoreType, { main: SelectStoreTypeStep }, { disableBack: true });

  if (storeType === StoreType.EXISTING) {
    flow.step(StepId.CurrentPlatform, { main: CurrentPlatformStep });
    flow.task(ActionId.PlatformDomainToSiteName, () => {
      const { currentPlatformUrl } = useAppStore.getState();
      if (currentPlatformUrl && isValidURL(currentPlatformUrl)) {
        const siteName = toValidURL(currentPlatformUrl)?.hostname?.replace('www.', '')?.split('.')?.[0];
        useAppStore.setState({ siteName });
      }
    });
  }

  flow.step(StepId.SelectCatalogSize, { main: SelectCatalogSizeStep });

  if (catalogSize === CatalogSize.SingleProduct && isCreateSingleProductEnabled) {
    flow.step(StepId.CreateProductUpload, { main: CreateProductUploadStep });
    flow.step(
      StepId.CreateProductForm,
      { main: CreateProductFormStep },
      { countInProgress: StepId.CreateProductUpload },
    );
  }

  if (!shouldAddSingleProductToStore) {
    flow.step(StepId.SelectSellingType, { main: SelectSellingTypeStep });
  }

  flow.step(StepId.SelectCategory, { main: SelectCategoryStep }, {}, () => {
    return useProductStore.getState().shouldAddSingleProductToStore;
  });

  if (!window.__IS_LOGGED_IN__) {
    flow.action(ActionId.RedirectToLogin, () => redirects.redirectToLogin(StepId.SelectTemplate));
  }

  flow.task(ActionId.SetTemplateSlug, () => {
    const { category } = useAppStore.getState();
    const templateSlug = templatesByCategory[category ?? Category.Other][0];
    useAppStore.setState({ templateSlug });
  });

  flow.step(StepId.SelectTemplate, { main: SelectTemplateStep, side: SelectTemplateStepPreview }, { splitRatio: 25 });

  flow.step(StepId.SelectSiteName, { main: SelectSiteNameStep, side: SelectTemplateStepPreview }, { splitRatio: 25 });

  flow.step(
    StepId.SiteSetup,
    { main: SiteSetupStep, side: SelectTemplateStepPreview },
    { hideProgress: true, splitRatio: 25 },
  );

  if (isMobile) {
    flow.step(StepId.SiteReady, { main: SiteReadyStepMobile }, { hideProgress: true });
  } else {
    flow.step(StepId.SiteReady, { side: SiteReadyStep }, { splitRatio: 0, hideProgress: true });
  }

  flow.action(ActionId.RedirectToUpgradePlan, redirects.redirectToUpgradePlan);
  flow.action(ActionId.RedirectToDashboard, redirects.redirectToDashboard);
  return flow.build();
};
